import React, { useState, useEffect, useMemo } from "react";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import Loading from "../../customComponents/Loading";
import DynamicTable from "../../components/DynamicTable";
import { useLocation, useNavigate } from "react-router-dom";
import apiService from "../../services/apiService";
import { Key } from "lucide-react";
import { withSearchIcon } from "../../utils/helper";
import { keyboard } from "@testing-library/user-event/dist/keyboard";

export default function EventPurchasedTickets() {
  const [tickets, setTickets] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const navigate = useNavigate();
  const location = useLocation();
  const queryEventId = new URLSearchParams(location.search).get("eventId");

  const [eventId, setEventId] = useState<any>(queryEventId);
  const columns = [
    { key: "id", label: "ID" },
    { key: "eventId", label: "Event Id" },
    { key: "eventName", label: withSearchIcon("Event Name") },
    {
      key: "ticketTypeId",
      label: "TicketType Id",
    },
    { key: "ticketTypeName", label: withSearchIcon("Ticket Type") },
    {
      key: "purchasedUserId",
      label: "Purchased User Id",
    },
    { key: "purchaseUserName", label: withSearchIcon("Purchased By") },
    { key: "ticketTypeTotalTickets", label: "Total Tickets" },
    {
      key: "taxAmount",
      label: "Tax",
      render: (val?: string) => (val ? `${val}` : "-"),
      isDecimal: true,
      isAmount: true,
    },

    {
      key: "stripeTaxCalculationId",
      label: "Stripe Tax Calculation Id",
      className: "text-[10px]",
      render: (_: any, row: any) => {
        return row?.stripeTaxCalculationId ? (
          <div className="flex gap-1">
            {row?.stripeTaxCalculationId}{" "}
            {/* <button
              onClick={() => handleCopyStripeTaxId(row?.stripeTaxCalculationId)}
              className="px-1 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              Copy
            </button> */}
          </div>
        ) : "";
      },
    },
    {
      key: "amount",
      label: "Base Price",
      render: (val?: string) => (val ? `${val}` : "-"),
      isDecimal: true,
      isAmount: true,
    },
    {
      key: "otherFee",
      label: "Socha Fees",
      render: (val?: string) => (val ? `${val}` : "-"),
      isDecimal: true,
      isAmount: true,
    },
    {
      key: "totalAmount",
      label: "Total Amount",
      render: (val?: string) => (val ? `${val}` : "-"),
      isDecimal: true,
      isAmount: true,
    },

    {
      key: "promoCodeId",
      label: "Promocode Id",
      render: (val?: string) => (val ? `${val}` : "-"),
    },

    {
      key: "promoCodeName",
      label: "Promocode Name",
      render: (val?: string) => (val ? `${val}` : "-"),
    },
    {
      key: "discountPercentage",
      label: "DiscountPercentage (%)",
      render: (val: number) =>
        val != null && Number(val) > 0 ? `${val}%` : "-",

      // render: (val?: string) => (val ? `${val}%` : "-"),
    },

    {
      key: "discountedAmount",
      label: "Discounted Amount",
      render: (val?: string) => (val ? `${val}` : "-"),
      isDecimal: true,
      isAmount: true,
    },
    {
      key: "paymentIntend",
      label: "Payment Intent",
      render: (val?: string) => (val ? `${val}` : "-"),
    },
    {
      key: "paymentStatus",
      label: "Payment Status",
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            val === "Paid"
              ? "bg-green-100 text-green-800"
              : val === "Pending"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-gray-100 text-gray-800"
          }`}
        >
          {val}
        </span>
      ),
    },
    { key: "paymentMethod", label: "Payment Method" },
    {
      key: "createdAt",
      label: "Created At(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "updatedAt",
      label: "Updated At(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
  ];

  useEffect(() => {
    fetchGuests(eventId, page, search);
  }, [eventId, page, search]);

    // ✅ Copy Stripe Tax Calculation Id
    const handleCopyStripeTaxId = (text: string) => {
      navigator.clipboard.writeText(text);
      toast.success("Stripe Tax Calculation Id copied!");
    };

  const fetchGuests = async (
    eventId?: number,
    activePageNo?: number,
    searchTerm?: any,
  ) => {
    try {
      setLoading(true);

      // Build params conditionally
      const params: any = {
        page: activePageNo,
        limit: 50,
        search: searchTerm,
        ...(eventId ? { eventId } : {}), // ✅ adds eventId only if it exists
      };

      const response: any = await apiService.get(
        "/event/ticket/get-event-purchased-tickets",
        params,
      );

      setTickets(response?.data?.data || []);
      setTotalRecords(response?.data?.totalCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching guests:", error);
      toast.error("Failed to fetch guests");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchKey?: any) => {
    console.log("Searching for Event ID:", searchKey);

    const params = new URLSearchParams(location.search);

    if (searchKey && searchKey.trim() !== "") {
      // ✅ Add/replace eventId
      params.set("eventId", searchKey.trim());
    } else {
      // ❌ searchKey empty → remove eventId
      params.delete("eventId");
    }

    // 🚀 Update URL (no reload)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });

    // ✅ Update local state if needed
    setEventId(searchKey || "");
    setPage(1);
  };
  return (
    <Layout
      pageTitle="Purchased Tickets"
      eventId={queryEventId || eventId}
      searchByEventId={{
        label: "Search by Event ID",
        onClick: (val?: any) => {
          handleSearch(val);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-2 px-4">
        <DynamicTable
          title={`Purchased Tickets`}
          data={tickets}
          columns={columns}
          pageSize={pageSize}
          loading={loading}
          activeNUmber={page}
          onPageChange={(newPage) => setPage(newPage)}
          totalPages={totalPages}
          totalRecords={totalRecords}
          onSearch={(val?: any) => {
            setPage(1);
            setSearch(val);
          }}
        />
      </div>
    </Layout>
  );
}
